import { Box, TableCell, TableRow, Typography, Popover, MenuList, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { type Schema } from '../../../../../amplify/data/resource';
import { useSnackbar } from 'notistack';
import Button from '../../../../components/custom/Button';
import Select from '../../../../components/custom/Select';
import ReactLoading from 'react-loading';

const apiClient = generateClient<Schema>();

interface RowProps {
  client: any;
}

const ClientItem: React.FC<RowProps> = ({ client }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openPaymentPlanDialog, setOpenPaymentPlanDialog] = useState(false);
  const [paymentPlans, setPaymentPlans] = useState<any[]>([]);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState('');
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [currentPaymentPlan, setCurrentPaymentPlan] = useState<any>(null);
  const [loadingCurrentPlan, setLoadingCurrentPlan] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePaymentPlan = () => {
    setOpenPaymentPlanDialog(true);
    setSelectedPaymentPlan(client.paymentPlanId || '');
    handleMenuClose();
  };

  const handleClosePaymentPlanDialog = () => {
    setOpenPaymentPlanDialog(false);
    setSelectedPaymentPlan('');
  };

  // Загружаем текущий payment plan клиента
  useEffect(() => {
    const fetchCurrentPaymentPlan = async () => {
      if (client.paymentPlanId) {
        setLoadingCurrentPlan(true);
        try {
          const { data: plan, errors } = await apiClient.models.PaymentPlan.get({
            id: client.paymentPlanId
          });

          if (errors && errors.length > 0) {
            console.error('Errors fetching current payment plan:', errors);
          } else {
            setCurrentPaymentPlan(plan);
          }
        } catch (error) {
          console.error('Exception during fetching current payment plan:', error);
        } finally {
          setLoadingCurrentPlan(false);
        }
      }
    };

    fetchCurrentPaymentPlan();
  }, [client.paymentPlanId]);

  // Загружаем payment plans при открытии диалога
  useEffect(() => {
    if (openPaymentPlanDialog && !loadingPlans) {
      const fetchPaymentPlans = async () => {
        setLoadingPlans(true);
        try {
          const { data: fetchedPlans, errors } = await apiClient.models.PaymentPlan.list({});

          if (errors && errors.length > 0) {
            console.error('Errors fetching payment plans:', errors);
            enqueueSnackbar('שגיאה בטעינת תוכניות התשלום', { variant: 'error' });
          } else {
            setPaymentPlans(fetchedPlans || []);
          }
        } catch (error) {
          console.error('Exception during fetching payment plans:', error);
          enqueueSnackbar('שגיאה בטעינת תוכניות התשלום', { variant: 'error' });
        } finally {
          setLoadingPlans(false);
        }
      };

      fetchPaymentPlans();
    }
  }, [openPaymentPlanDialog, enqueueSnackbar]);

  const handleUpdatePaymentPlan = async () => {
    if (!selectedPaymentPlan) {
      enqueueSnackbar('יש לבחור תוכנית תשלום', { variant: 'warning' });
      return;
    }

    setUpdating(true);
    try {
      const { data: updatedUser, errors } = await apiClient.models.User.update({
        id: client.id,
        paymentPlanId: selectedPaymentPlan,
      });

      if (errors && errors.length > 0) {
        console.error('Errors updating user:', errors);
        enqueueSnackbar('שגיאה בעדכון תוכנית התשלום', { variant: 'error' });
      } else {
        // Обновляем текущий payment plan
        const updatedPlan = paymentPlans.find(plan => plan.id === selectedPaymentPlan);
        if (updatedPlan) {
          setCurrentPaymentPlan(updatedPlan);
        }
        
        enqueueSnackbar('תוכנית התשלום עודכנה בהצלחה', { variant: 'success' });
        handleClosePaymentPlanDialog();
      }
    } catch (error) {
      console.error('Exception during updating user:', error);
      enqueueSnackbar('שגיאה בעדכון תוכנית התשלום', { variant: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  // Преобразуем payment plans в формат, который ожидает кастомный Select
  const selectOptions = paymentPlans.map(plan => ({
    label: plan.title,
    value: plan.id
  }));

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: '#F2F4F7',
          borderRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          cursor: 'pointer',
        }}
        onClick={() => navigate(`/clients/${client.id}`)}
      >
        <TableCell
          sx={{
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {client.companyName}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {client?.companyRegistrationNumber}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {client.authorizedSignatoryName}
            </Typography>
          </Box>
        </TableCell>
        
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {client.authorizedSignatoryPhone}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {client.email}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {loadingCurrentPlan ? 'טוען...' : (currentPaymentPlan?.title || 'לא הוגדר')}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              {format(new Date(client.createdAt), 'dd/MM/yyyy', {
                locale: he,
              })}
            </Typography>
          </Box>
        </TableCell>
        
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            borderTopLeftRadius: '10px',
            borderBottomLeftRadius: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '45px',
                height: '45px',
                background: 'white',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
              onClick={handleMenuClick}
            >
              <Icon
                icon="mdi:dots-horizontal"
                width={20}
                height={20}
                color="#666"
              />
            </Box>
          </Box>
        </TableCell>
      </TableRow>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            minWidth: '200px',
            mt: 1,
          },
        }}
      >
        <MenuList sx={{ py: 1 }}>
          <MenuItem onClick={handlePaymentPlan} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: '36px' }}>
              <Icon icon="mdi:credit-card-outline" width={20} height={20} color="black" />
            </ListItemIcon>
            <ListItemText 
              primary="תוכנית תשלום"
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'AlmoniDL',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'right'
                } 
              }}
            />
          </MenuItem>
        </MenuList>
      </Popover>

      <Dialog
        open={openPaymentPlanDialog}
        onClose={handleClosePaymentPlanDialog}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            minWidth: '400px',
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'AlmoniDL', 
          fontSize: '20px', 
          fontWeight: 700,
          textAlign: 'center',
          pb: 1
        }}>
          בחירת תוכנית תשלום
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 1, minHeight: '150px' }}>
          {loadingPlans ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <ReactLoading
                type={'bubbles'}
                color={'#94C729'}
                height={50}
                width={100}
              />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Select
                id="payment-plan-select"
                label="בחר תוכנית תשלום"
                value={selectedPaymentPlan}
                onChange={setSelectedPaymentPlan}
                options={selectOptions}
                sx={{ width: '100%' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            onClick={handleUpdatePaymentPlan}
            customVariant="default"
            disabled={!selectedPaymentPlan || updating}
            sx={{
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
              minWidth: '100px'
            }}
          >
            {updating ? (
              <ReactLoading
                type={'bubbles'}
                color={'white'}
                height={20}
                width={40}
              />
            ) : (
              'שמירה'
            )}
          </Button>
          <Button
            onClick={handleClosePaymentPlanDialog}
            customVariant="light"
            disabled={updating}
            sx={{
              fontFamily: 'AlmoniDL',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
            }}
          >
            ביטול
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientItem;
